// @ts-nocheck
import React, { useState, useEffect, useRef } from 'react';
import { Card, Row, Col, Table, Select, DatePicker, Button, Statistic, Progress, message, Spin, Badge } from 'antd';
import { DownloadOutlined, DollarOutlined, RiseOutlined, FileTextOutlined, BarChartOutlined, CheckCircleOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import type { ColumnsType } from 'antd/es/table';
import { Title } from '../components/design-system/typography';
import { StatusPill } from '../components/design-system/StatusPill';
import LoadingState from '../components/design-system/LoadingState';
import { ErrorDisplay } from '../components/design-system/ErrorBoundary';
import { ResponsiveGrid, ResponsiveTable, useResponsive } from '../components/design-system/ResponsiveContainer';
import { colors, spacing } from '../components/design-system/tokens';
import { queryConfigs, queryKeys } from '../utils/queryConfig';
import { AriaHelper, ResponsiveHelper } from '../utils/accessibility';

const { RangePicker } = DatePicker;
const { Option } = Select;

interface FinancialSummary {
  totalRevenue: number;
  totalCommission: number;
  totalProfit: number;
  activeContracts: number;
  pendingPayments: number;
  overduePayments: number;
  averageMargin: number;
  monthlyGrowth: number;
  xeroSyncStatus: {
    status: string;
    lastSync: string | null;
    nextSync: string | null;
  };
}

interface CommissionData {
  id: string;
  contractRef: string;
  supplier: string;
  buyer: string;
  commodity: string;
  commissionAmount: number;
  commissionRate: number;
  totalValue: number;
  status: 'PENDING' | 'PAID' | 'OVERDUE';
  dueDate: string;
  paidDate?: string;
}

interface RevenueData {
  month: string;
  revenue: number;
  commission: number;
  profit: number;
  contracts: number;
}

// API client functions
const fetchFinancialSummary = async (period: string): Promise<FinancialSummary> => {
  const token = localStorage.getItem('atlas_token');
  if (!token) throw new Error('No authentication token');
  
  const response = await fetch(`/api/v1/financial/summary?period=${period}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch financial summary');
  }
  
  return response.json();
};

const fetchCommissionData = async (period: string, status?: string): Promise<{ commissions: CommissionData[]; pagination: any }> => {
  const token = localStorage.getItem('atlas_token');
  if (!token) throw new Error('No authentication token');
  
  const params = new URLSearchParams({ period });
  if (status && status !== 'overview') {
    params.append('status', status.toUpperCase());
  }
  
  const response = await fetch(`/api/v1/financial/commissions?${params.toString()}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch commission data');
  }
  
  return response.json();
};

const fetchRevenueData = async (period: string): Promise<{ data: RevenueData[] }> => {
  const token = localStorage.getItem('atlas_token');
  if (!token) throw new Error('No authentication token');
  
  const response = await fetch(`/api/v1/financial/revenue-trend?period=${period}`, {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });
  
  if (!response.ok) {
    throw new Error('Failed to fetch revenue data');
  }
  
  return response.json();
};

const FinancialOverview: React.FC = () => {
  const [dateRange, setDateRange] = useState<string>('3months');
  const [reportType, setReportType] = useState<string>('overview');
  const financialRef = useRef<HTMLDivElement>(null);
  const { isMobile, isTablet } = useResponsive();

  // Use React Query for data fetching with optimized caching
  const { data: financialSummary, isLoading: summaryLoading, error: summaryError } = useQuery({
    queryKey: queryKeys.financial.summary(dateRange),
    queryFn: () => fetchFinancialSummary(dateRange),
    ...queryConfigs.dashboardData, // Financial summary is dashboard data
  });

  const { data: commissionResponse, isLoading: commissionsLoading, error: commissionsError } = useQuery({
    queryKey: queryKeys.financial.commissions(dateRange, reportType),
    queryFn: () => fetchCommissionData(dateRange, reportType),
    ...queryConfigs.businessData, // Commission details are business data
  });

  const { data: revenueResponse, isLoading: revenueLoading, error: revenueError } = useQuery({
    queryKey: queryKeys.financial.revenue(dateRange),
    queryFn: () => fetchRevenueData(dateRange),
    ...queryConfigs.dashboardData, // Revenue trends are dashboard data
  });

  const commissionData = commissionResponse?.commissions || [];
  const revenueData = revenueResponse?.data || [];
  const loading = summaryLoading || commissionsLoading || revenueLoading;

  // Announce data updates to screen readers
  useEffect(() => {
    if (financialSummary) {
      const { totalRevenue, totalCommission, pendingPayments } = financialSummary;
      AriaHelper.announce(
        `Financial data loaded. Total revenue: $${totalRevenue?.toLocaleString()}, Total commission: $${totalCommission?.toLocaleString()}, Pending payments: ${pendingPayments}`,
        'polite'
      );
    }
  }, [financialSummary]);

  // Show error message if any API calls fail
  useEffect(() => {
    if (summaryError) {
      message.error(`Failed to load financial summary: ${summaryError.message}`);
      AriaHelper.announce(`Error loading financial summary: ${summaryError.message}`, 'assertive');
    }
    if (commissionsError) {
      message.error(`Failed to load commission data: ${commissionsError.message}`);
      AriaHelper.announce(`Error loading commission data: ${commissionsError.message}`, 'assertive');
    }
    if (revenueError) {
      message.error(`Failed to load revenue data: ${revenueError.message}`);
      AriaHelper.announce(`Error loading revenue data: ${revenueError.message}`, 'assertive');
    }
  }, [summaryError, commissionsError, revenueError]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusVariant = (status: CommissionData['status']) => {
    switch (status) {
      case 'PAID':
        return 'positive';
      case 'PENDING':
        return 'neutral';
      case 'OVERDUE':
        return 'negative';
      default:
        return 'neutral';
    }
  };

  const commissionColumns: ColumnsType<CommissionData> = [
    {
      title: 'Contract',
      dataIndex: 'contractRef',
      key: 'contractRef',
      width: 120,
      render: (text: string) => (
        <div style={{ fontWeight: 600, color: colors.text.primary }}>
          {text}
        </div>
      ),
    },
    {
      title: 'Parties',
      key: 'parties',
      width: 300,
      render: (_, record: CommissionData) => (
        <div>
          <div style={{ fontSize: '13px', color: colors.text.primary }}>
            {record.supplier}
          </div>
          <div style={{ fontSize: '12px', color: colors.text.secondary, margin: '2px 0' }}>
            ↓
          </div>
          <div style={{ fontSize: '13px', color: colors.text.secondary }}>
            {record.buyer}
          </div>
        </div>
      ),
    },
    {
      title: 'Commodity',
      dataIndex: 'commodity',
      key: 'commodity',
      width: 150,
    },
    {
      title: 'Total Value',
      dataIndex: 'totalValue',
      key: 'totalValue',
      width: 120,
      align: 'right',
      render: (value: number) => (
        <div style={{ fontFamily: 'monospace', fontWeight: 500 }}>
          {formatCurrency(value)}
        </div>
      ),
    },
    {
      title: 'Commission',
      key: 'commission',
      width: 150,
      align: 'right',
      render: (_, record: CommissionData) => (
        <div>
          <div style={{ fontFamily: 'monospace', fontWeight: 600, color: colors.status.positive }}>
            {formatCurrency(record.commissionAmount)}
          </div>
          <div style={{ fontSize: '11px', color: colors.text.secondary }}>
            {record.commissionRate}%
          </div>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: CommissionData['status']) => (
        <StatusPill variant={getStatusVariant(status)}>
          {status.charAt(0) + status.slice(1).toLowerCase()}
        </StatusPill>
      ),
    },
    {
      title: 'Due Date',
      dataIndex: 'dueDate',
      key: 'dueDate',
      width: 100,
      render: (date: string, record: CommissionData) => (
        <div>
          <div style={{ fontSize: '13px', color: colors.text.primary }}>
            {formatDate(date)}
          </div>
          {record.paidDate && (
            <div style={{ fontSize: '11px', color: colors.status.positive }}>
              Paid {formatDate(record.paidDate)}
            </div>
          )}
        </div>
      ),
    },
  ];

  const revenueColumns: ColumnsType<RevenueData> = [
    {
      title: 'Month',
      dataIndex: 'month',
      key: 'month',
      width: 120,
    },
    {
      title: 'Revenue',
      dataIndex: 'revenue',
      key: 'revenue',
      width: 120,
      align: 'right',
      render: (value: number) => (
        <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>
          {formatCurrency(value)}
        </div>
      ),
    },
    {
      title: 'Commission',
      dataIndex: 'commission',
      key: 'commission',
      width: 120,
      align: 'right',
      render: (value: number) => (
        <div style={{ fontFamily: 'monospace', fontWeight: 600, color: colors.status.positive }}>
          {formatCurrency(value)}
        </div>
      ),
    },
    {
      title: 'Profit',
      dataIndex: 'profit',
      key: 'profit',
      width: 120,
      align: 'right',
      render: (value: number) => (
        <div style={{ fontFamily: 'monospace', fontWeight: 600, color: colors.status.warning }}>
          {formatCurrency(value)}
        </div>
      ),
    },
    {
      title: 'Contracts',
      dataIndex: 'contracts',
      key: 'contracts',
      width: 100,
      align: 'center',
      render: (value: number) => (
        <div style={{ fontWeight: 600 }}>
          {value}
        </div>
      ),
    },
  ];

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '400px'
      }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{
      background: colors.background.primary,
      minHeight: 'calc(100vh - 112px)',
      padding: spacing['2xl']
    }}>
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        {/* Page Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: spacing['2xl'] 
        }}>
          <div>
            <Title>Financial Overview</Title>
            <p style={{
              color: colors.text.secondary,
              fontSize: '16px',
              marginTop: spacing.sm
            }}>
              Track revenue, commissions, and financial performance
            </p>
          </div>
          
          <div style={{ display: 'flex', gap: spacing.lg, alignItems: 'center' }}>
            <Select
              value={dateRange}
              onChange={setDateRange}
              style={{ width: 150 }}
            >
              <Option value="1month">Last Month</Option>
              <Option value="3months">Last 3 Months</Option>
              <Option value="6months">Last 6 Months</Option>
              <Option value="1year">Last Year</Option>
            </Select>
            
            <Button 
              type="primary" 
              icon={<DownloadOutlined />}
              onClick={() => message.info('Export functionality coming soon')}
            >
              Export Report
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        {financialSummary && (
          <Row gutter={[24, 24]} style={{ marginBottom: spacing['2xl'] }}>
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Revenue"
                  value={financialSummary.totalRevenue}
                  formatter={(value) => formatCurrency(Number(value))}
                  prefix={<DollarOutlined style={{ color: colors.status.positive }} />}
                  valueStyle={{ color: colors.text.primary }}
                />
              </Card>
            </Col>
            
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Total Commission"
                  value={financialSummary.totalCommission}
                  formatter={(value) => formatCurrency(Number(value))}
                  prefix={<RiseOutlined style={{ color: colors.status.warning }} />}
                  valueStyle={{ color: colors.status.positive }}
                />
              </Card>
            </Col>
            
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Net Profit"
                  value={financialSummary.totalProfit}
                  formatter={(value) => formatCurrency(Number(value))}
                  prefix={<BarChartOutlined style={{ color: colors.status.warning }} />}
                  valueStyle={{ color: colors.status.warning }}
                />
              </Card>
            </Col>
            
            <Col xs={24} sm={12} lg={6}>
              <Card>
                <Statistic
                  title="Active Contracts"
                  value={financialSummary.activeContracts}
                  prefix={<FileTextOutlined style={{ color: colors.text.secondary }} />}
                  valueStyle={{ color: colors.text.primary }}
                />
              </Card>
            </Col>
          </Row>
        )}

        {/* Performance Metrics */}
        {financialSummary && (
          <Row gutter={[24, 24]} style={{ marginBottom: spacing['2xl'] }}>
            <Col xs={24} lg={12}>
              <Card title="Average Margin" size="small">
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
                  <Progress
                    type="circle"
                    percent={financialSummary.averageMargin * 10} // Scale for visual
                    format={() => `${financialSummary.averageMargin}%`}
                    strokeColor={colors.status.positive}
                    size={80}
                  />
                  <div>
                    <div style={{ fontSize: '14px', color: colors.text.secondary }}>
                      Industry average: 4.2%
                    </div>
                    <div style={{ fontSize: '12px', color: colors.status.positive }}>
                      +24% above average
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
            
            <Col xs={24} lg={12}>
              <Card title="Monthly Growth" size="small">
                <div style={{ display: 'flex', alignItems: 'center', gap: spacing.lg }}>
                  <Progress
                    type="circle"
                    percent={Math.min(financialSummary.monthlyGrowth * 8, 100)} // Scale for visual
                    format={() => `${financialSummary.monthlyGrowth}%`}
                    strokeColor={colors.status.warning}
                    size={80}
                  />
                  <div>
                    <div style={{ fontSize: '14px', color: colors.text.secondary }}>
                      Compared to last period
                    </div>
                    <div style={{ fontSize: '12px', color: colors.status.positive }}>
                      Strong growth trend
                    </div>
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Payment Status Summary */}
        {financialSummary && (
          <Row gutter={[24, 24]} style={{ marginBottom: spacing['2xl'] }}>
            <Col xs={24} lg={18}>
              <Card title="Payment Status">
                <Row gutter={[24, 16]}>
                  <Col xs={24} sm={8}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 600, color: colors.status.neutral }}>
                        {financialSummary.pendingPayments}
                      </div>
                      <div style={{ color: colors.text.secondary }}>Pending Payments</div>
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 600, color: colors.status.negative }}>
                        {financialSummary.overduePayments}
                      </div>
                      <div style={{ color: colors.text.secondary }}>Overdue Payments</div>
                    </div>
                  </Col>
                  <Col xs={24} sm={8}>
                    <div style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: '20px', fontWeight: 600, color: colors.status.positive }}>
                        {commissionData.filter(c => c.status === 'PAID').length}
                      </div>
                      <div style={{ color: colors.text.secondary }}>Completed Payments</div>
                    </div>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} lg={6}>
              <Card title="Xero Integration">
                <div style={{ textAlign: 'center' }}>
                  <Badge 
                    status={financialSummary.xeroSyncStatus.status === 'connected' ? 'success' : 'error'} 
                    text={
                      <span style={{ fontWeight: 600 }}>
                        {financialSummary.xeroSyncStatus.status === 'connected' ? 'Connected' : 'Disconnected'}
                      </span>
                    }
                  />
                  <div style={{ marginTop: '8px', fontSize: '12px', color: colors.text.secondary }}>
                    <div>
                      <ClockCircleOutlined style={{ marginRight: '4px' }} />
                      Last sync: {financialSummary.xeroSyncStatus.lastSync 
                        ? new Date(financialSummary.xeroSyncStatus.lastSync).toLocaleString()
                        : 'Never'
                      }
                    </div>
                    {financialSummary.xeroSyncStatus.nextSync && (
                      <div style={{ marginTop: '4px' }}>
                        Next sync: {new Date(financialSummary.xeroSyncStatus.nextSync).toLocaleTimeString()}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            </Col>
          </Row>
        )}

        {/* Commission Details Table */}
        <Card 
          title="Commission Details" 
          style={{ marginBottom: spacing['2xl'] }}
          extra={
            <Select
              value={reportType}
              onChange={setReportType}
              style={{ width: 150 }}
            >
              <Option value="overview">All Commissions</Option>
              <Option value="pending">Pending Only</Option>
              <Option value="overdue">Overdue Only</Option>
            </Select>
          }
        >
          <Table
            columns={commissionColumns}
            dataSource={commissionData.filter(item => {
              if (reportType === 'pending') return item.status === 'PENDING';
              if (reportType === 'overdue') return item.status === 'OVERDUE';
              return true;
            })}
            rowKey="id"
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) =>
                `${range[0]}-${range[1]} of ${total} commissions`,
            }}
            scroll={{ x: 1000 }}
            size="middle"
          />
        </Card>

        {/* Revenue Trend */}
        <Card title="Revenue Trend">
          <Table
            columns={revenueColumns}
            dataSource={revenueData}
            rowKey="month"
            pagination={false}
            size="middle"
            summary={(pageData) => {
              const totalRevenue = pageData.reduce((sum, item) => sum + item.revenue, 0);
              const totalCommission = pageData.reduce((sum, item) => sum + item.commission, 0);
              const totalProfit = pageData.reduce((sum, item) => sum + item.profit, 0);
              const totalContracts = pageData.reduce((sum, item) => sum + item.contracts, 0);

              return (
                <Table.Summary.Row style={{ backgroundColor: colors.background.secondary }}>
                  <Table.Summary.Cell index={0}>
                    <div style={{ fontWeight: 600 }}>TOTAL</div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <div style={{ fontFamily: 'monospace', fontWeight: 600 }}>
                      {formatCurrency(totalRevenue)}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={2} align="right">
                    <div style={{ fontFamily: 'monospace', fontWeight: 600, color: colors.status.positive }}>
                      {formatCurrency(totalCommission)}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={3} align="right">
                    <div style={{ fontFamily: 'monospace', fontWeight: 600, color: colors.status.warning }}>
                      {formatCurrency(totalProfit)}
                    </div>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={4} align="center">
                    <div style={{ fontWeight: 600 }}>
                      {totalContracts}
                    </div>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              );
            }}
          />
        </Card>
      </div>
    </div>
  );
};

export default FinancialOverview;